import { defineStore } from "pinia";
import { ref } from "vue";
import { useAuth } from "@/composables/useAuth";
import AzureGraphDataFetcher from "@/services/connectors/azureGraphConnector";

export const useAzureStore = defineStore("azure", () => {
    let graphFetcher: AzureGraphDataFetcher | null = null;
    const { graphToken } = useAuth();

    const getGraphFetcher = (): AzureGraphDataFetcher => {
        if (!graphFetcher) {
            graphFetcher = new AzureGraphDataFetcher(graphToken.value);
        }
        return graphFetcher;
    };

    const pageDescription = ref({
        data: "",
        loading: false,
        error: null as any,
    });
    const userPhoto = ref({
        blob: null as Blob | null,
        loader: true,
    });
    const isOwner = ref(false);
    const isAdmin = ref(false);

    const loadPageDescription = async (id: number) => {
        pageDescription.value.loading = true;
        try {
            const result = await getGraphFetcher().getPageDescription(String(id));
            pageDescription.value.data = result.fields.Content.replace(
                /src="\/:i:\//g,
                'src="https://abb.sharepoint.com/:i:/'
            )
                .replace(/src="\/sites\//g, 'src="https://abb.sharepoint.com/sites/')
                .replace(/href="\/sites\//g, 'href="https://abb.sharepoint.com/sites/')
                .replace(/<a/g, '<a target="_blank" rel="noopener noreferrer"');
        } catch (error: any) {
            pageDescription.value.error = error;
        } finally {
            pageDescription.value.loading = false;
        }
    };

    const loadMyPhoto = async () => {
        try {
            userPhoto.value.loader = true;
            const result = await getGraphFetcher().loadMyPhoto();
            if (result) {
                userPhoto.value.blob = result;
                userPhoto.value.loader = false;
            } else {
                console.error("Failed to fetch photo");
            }
        } catch (error) {
            console.error("Error fetching photo:", error);
        }
    };

    const loadIsUserOwner = async () => {
        try {
            isOwner.value = await getGraphFetcher().isCurrentUserMemberOfGroup();
        } catch (error) {
            console.error("Error checking user ownership:", error);
            return false;
        }
    };

    const loadIsUserAdmin = async () => {
        try {
            isAdmin.value = await getGraphFetcher().isCurrentUserAdminMember();
        } catch (error) {
            console.error("Error checking admin membership:", error);
            return false;
        }
    };

    // Getters
    const getPhoto = () => userPhoto.value.blob;
    const getPhotoLoader = () => userPhoto.value.loader;
    const getPageDescription = () => pageDescription.value.data;
    const isUserOwner = () => isOwner.value;
    const isUserAdmin = () => isAdmin.value;

    return {
        pageDescription,
        userPhoto,
        isOwner,
        isAdmin,
        loadPageDescription,
        loadMyPhoto,
        loadIsUserOwner,
        loadIsUserAdmin,
        getPhoto,
        getPhotoLoader,
        getPageDescription,
        isUserOwner,
        isUserAdmin,
    };
});
